import { useAuth0 } from '@auth0/auth0-react';
import { useConsumeOrganisationSequence, useLocations, useParties } from '@my/directory-api/hooks';
import { useCargoDefinitions, useImportOrders } from '@my/tms-api/hooks';
import { Button, cn, ErrorBanner } from '@my/ui';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { Check, CircleCheck, CircleX, Loader2, MinusCircle, UploadCloud } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { parseImportFile } from '@/features/parties/import/lib/parseImportFile';
import type { ParsedFile } from '@/features/parties/import/lib/types';
import { buildPlaceIdIndex, resolveAddresses, type AddressResolution } from './lib/addresses';
import { commitOrders, type CommitDeps } from './lib/commit';
import { parseJobs } from './lib/parseJobs';
import { buildDrafts, buildResolveContext } from './lib/resolve';
import { isResolvable, type DraftStatus, type OrderDraft } from './lib/types';

type Step = 'upload' | 'review' | 'commit';
type AddressMap = Map<string, AddressResolution>;

/**
 * Orders tab of Settings → Import. Bulk-creates orders (header + collection /
 * delivery tasks + cargo) from a jobs file:
 *   upload → review & resolve (customer + cargo matched; addresses geocoded and
 *   matched against existing locations) → commit (per-order results, retry).
 *
 * Renders as a tab section — the page header + tab strip live in `ImportPage`.
 */
export function OrdersImportTab() {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const [step, setStep] = useState<Step>('upload');
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [drafts, setDrafts] = useState<OrderDraft[]>([]);
  const [addressRes, setAddressRes] = useState<AddressMap>(new Map());
  const [geoProgress, setGeoProgress] = useState<{ done: number; total: number } | null>(null);

  // Resolution sources — parties (any type), cargo definitions, existing
  // locations (for place-id dedup), and the browser geocoder.
  const partiesQuery = useParties(orgId ?? null, null);
  const cargoQuery = useCargoDefinitions(orgId ?? null);
  const locationsQuery = useLocations(orgId ?? null);
  const ctxReady = partiesQuery.isSuccess && cargoQuery.isSuccess;

  const geocodingLib = useMapsLibrary('geocoding');
  const geocoder = useMemo(() => (geocodingLib ? new geocodingLib.Geocoder() : null), [geocodingLib]);

  // Build drafts once the file is parsed and the resolution sources are ready.
  useEffect(() => {
    if (!parsed || !ctxReady || drafts.length > 0) return;
    const ctx = buildResolveContext(partiesQuery.data ?? [], cargoQuery.data ?? []);
    setDrafts(buildDrafts(parseJobs(parsed), ctx));
  }, [parsed, ctxReady, drafts.length, partiesQuery.data, cargoQuery.data]);

  // Geocode every distinct address and match against existing locations.
  const runGeocode = useCallback(async () => {
    if (!geocoder || drafts.length === 0) return;
    const texts = new Set<string>();
    for (const d of drafts) {
      if (d.job.collectionAddress.trim()) texts.add(d.job.collectionAddress.trim());
      if (d.job.deliveryAddress.trim()) texts.add(d.job.deliveryAddress.trim());
    }
    const index = buildPlaceIdIndex(locationsQuery.data ?? []);
    setGeoProgress({ done: 0, total: texts.size });
    const map = await resolveAddresses(geocoder, [...texts], index, (done, total) =>
      setGeoProgress({ done, total }),
    );
    setAddressRes(map);
    setGeoProgress(null);
  }, [geocoder, drafts, locationsQuery.data]);

  // Auto-geocode once on entering review (when sources + geocoder are ready).
  useEffect(() => {
    if (
      step === 'review' &&
      geocoder &&
      locationsQuery.isSuccess &&
      drafts.length > 0 &&
      addressRes.size === 0 &&
      !geoProgress
    ) {
      void runGeocode();
    }
  }, [step, geocoder, locationsQuery.isSuccess, drafts.length, addressRes.size, geoProgress, runGeocode]);

  const patch = (key: string, p: Partial<OrderDraft>) =>
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...p } : d)));

  const toggleExclude = (key: string) =>
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, excluded: !d.excluded } : d)));

  const resetTo = (next: ParsedFile | null) => {
    setParsed(next);
    setDrafts([]);
    setAddressRes(new Map());
  };

  const STEPS: { id: Step; label: string }[] = [
    { id: 'upload', label: 'Upload' },
    { id: 'review', label: 'Review & resolve' },
    { id: 'commit', label: 'Import' },
  ];
  const stepIndex = STEPS.findIndex((s) => s.id === step);

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto pb-4">
      <ol className="flex items-center gap-2 text-xs">
        {STEPS.map((s, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          return (
            <li key={s.id} className="flex items-center gap-2">
              <span
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold',
                  active
                    ? 'bg-purple-600 text-white'
                    : done
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-500',
                )}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={active ? 'font-medium text-slate-900' : 'text-slate-500'}>{s.label}</span>
              {i < STEPS.length - 1 && <span className="mx-1 text-slate-300">→</span>}
            </li>
          );
        })}
      </ol>

      <section className="rounded-md border border-slate-200 bg-white p-4">
        {step === 'upload' && (
          <UploadStep parsed={parsed} onParsed={resetTo} onContinue={() => setStep('review')} />
        )}

        {step === 'review' &&
          (!ctxReady ? (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading customers and cargo types…
            </div>
          ) : !orgId ? (
            <ErrorBanner
              title="No organisation"
              message="Couldn't resolve the current organisation — sign in again or pick an organisation, then retry."
            />
          ) : (
            <ReviewStep
              drafts={drafts}
              addressRes={addressRes}
              geoProgress={geoProgress}
              geocoderReady={!!geocoder}
              onReGeocode={() => void runGeocode()}
              onToggleExclude={toggleExclude}
              onBack={() => setStep('upload')}
              onContinue={() => setStep('commit')}
            />
          ))}

        {step === 'commit' && orgId && (
          <CommitStep
            drafts={drafts}
            organisationId={orgId}
            addressRes={addressRes}
            patch={patch}
            onBack={() => setStep('review')}
            onDone={() => navigate('/orders')}
          />
        )}
      </section>
    </main>
  );
}

// ── Address helpers ───────────────────────────────────────────────────────--

function addrStatus(text: string, res: AddressMap): AddressResolution['status'] | 'pending' {
  return res.get(text.trim())?.status ?? 'pending';
}

function isReady(d: OrderDraft, res: AddressMap): boolean {
  if (d.excluded || !isResolvable(d)) return false;
  return (
    addrStatus(d.job.collectionAddress, res) !== 'failed' &&
    addrStatus(d.job.collectionAddress, res) !== 'pending' &&
    addrStatus(d.job.deliveryAddress, res) !== 'failed' &&
    addrStatus(d.job.deliveryAddress, res) !== 'pending'
  );
}

// ── Upload ───────────────────────────────────────────────────────────────────

function UploadStep({
  parsed,
  onParsed,
  onContinue,
}: {
  parsed: ParsedFile | null;
  onParsed: (p: ParsedFile | null) => void;
  onContinue: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      onParsed(await parseImportFile(file));
    } catch (err) {
      onParsed(null);
      setError(err instanceof Error ? err.message : 'Could not read the file.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files?.[0];
          if (file) void handleFile(file);
        }}
        className="flex w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center hover:border-slate-400"
      >
        {busy ? (
          <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
        ) : (
          <UploadCloud className="h-7 w-7 text-slate-400" />
        )}
        <span className="text-sm font-medium text-slate-700">Drop a jobs file here, or click to browse</span>
        <span className="text-xs text-slate-500">JSON, CSV, or Excel (.xlsx / .xls) — one row per order</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".json,.csv,.xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />

      {error && <ErrorBanner title="Couldn't read the file" message={error} />}

      {parsed && (
        <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <span className="text-slate-700">
            <span className="font-medium">{parsed.fileName}</span> — {parsed.rows.length} row
            {parsed.rows.length === 1 ? '' : 's'}
          </span>
          <Button type="button" onClick={onContinue} disabled={parsed.rows.length === 0}>
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Review ─────────────────────────────────────────────────────────────────--

function ReviewStep({
  drafts,
  addressRes,
  geoProgress,
  geocoderReady,
  onReGeocode,
  onToggleExclude,
  onBack,
  onContinue,
}: {
  drafts: OrderDraft[];
  addressRes: AddressMap;
  geoProgress: { done: number; total: number } | null;
  geocoderReady: boolean;
  onReGeocode: () => void;
  onToggleExclude: (key: string) => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const geocoding = geoProgress !== null;
  const geocoded = addressRes.size > 0 && !geocoding;

  const counts = useMemo(() => {
    let ready = 0;
    let unresolved = 0;
    let excluded = 0;
    let newAddrs = 0;
    let matchedAddrs = 0;
    let failedAddrs = 0;
    for (const [, r] of addressRes) {
      if (r.status === 'new') newAddrs += 1;
      else if (r.status === 'matched') matchedAddrs += 1;
      else failedAddrs += 1;
    }
    for (const d of drafts) {
      if (d.excluded) excluded += 1;
      else if (geocoded ? isReady(d, addressRes) : isResolvable(d)) ready += 1;
      else unresolved += 1;
    }
    return { ready, unresolved, excluded, newAddrs, matchedAddrs, failedAddrs };
  }, [drafts, addressRes, geocoded]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-slate-600">{drafts.length} orders</span>
        <span className="inline-flex items-center gap-1 text-emerald-700">
          <CircleCheck className="h-4 w-4" /> {counts.ready} ready
        </span>
        {counts.unresolved > 0 && (
          <span className="inline-flex items-center gap-1 text-red-600">
            <CircleX className="h-4 w-4" /> {counts.unresolved} need attention
          </span>
        )}
        {counts.excluded > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-600">
            <MinusCircle className="h-4 w-4" /> {counts.excluded} excluded
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-2">
          {geocoding ? (
            <span className="inline-flex items-center gap-2 text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" /> Geocoding {geoProgress?.done}/
              {geoProgress?.total}…
            </span>
          ) : (
            geocoded && (
              <span className="text-xs text-slate-500">
                addresses: {counts.matchedAddrs} existing · {counts.newAddrs} new
                {counts.failedAddrs > 0 && ` · ${counts.failedAddrs} failed`}
              </span>
            )
          )}
          <Button type="button" variant="ghost" onClick={onReGeocode} disabled={geocoding || !geocoderReady}>
            Re-geocode
          </Button>
        </span>
      </div>

      <div className="max-h-[30rem] overflow-auto rounded-md border border-slate-200">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-8 px-2 py-1.5" />
              <th className="px-2 py-1.5 font-semibold">Job</th>
              <th className="px-2 py-1.5 font-semibold">Customer</th>
              <th className="px-2 py-1.5 font-semibold">Cargo</th>
              <th className="px-2 py-1.5 font-semibold">Collection</th>
              <th className="px-2 py-1.5 font-semibold">Delivery</th>
              <th className="px-2 py-1.5 font-semibold">Qty</th>
              <th className="px-2 py-1.5 font-semibold">Temp</th>
            </tr>
          </thead>
          <tbody>
            {drafts.map((d) => (
              <tr key={d.key} className={cn('border-t border-slate-100', d.excluded && 'opacity-40')}>
                <td className="px-2 py-1">
                  <input
                    type="checkbox"
                    checked={!d.excluded}
                    onChange={() => onToggleExclude(d.key)}
                    aria-label={d.excluded ? 'Include' : 'Exclude'}
                  />
                </td>
                <td className="px-2 py-1 font-mono text-slate-600">{d.job.jobRef || '—'}</td>
                <td className="px-2 py-1">
                  <span className="text-slate-800">{d.customerLabel || '—'}</span>{' '}
                  <MatchBadge match={d.customerMatch} />
                </td>
                <td className="px-2 py-1">
                  {d.cargoMatch ? (
                    <span className="text-slate-800">
                      {d.cargoLabel}
                      {d.cargoVariantName && (
                        <span className="text-slate-400"> · {d.cargoVariantName}</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-red-600">{d.cargoLabel || '—'} (no match)</span>
                  )}
                </td>
                <AddrCell text={d.job.collectionAddress} date={d.job.collectionDate} res={addressRes} />
                <AddrCell text={d.job.deliveryAddress} date={d.job.deliveryDate} res={addressRes} />
                <td className="px-2 py-1 text-slate-600">{d.job.quantity ?? '—'}</td>
                <td className="px-2 py-1 text-slate-600">
                  {d.job.temperatureC == null ? 'Amb' : `${d.job.temperatureC}°C`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        Addresses are geocoded and matched against existing locations — an
        <span className="text-emerald-600"> existing</span> place is reused, a
        <span className="text-sky-600"> new</span> one is created at import. Orders that need
        attention (no customer / cargo match or an address that wouldn't geocode) are skipped.
      </p>

      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button type="button" onClick={onContinue} disabled={geocoding || counts.ready === 0}>
          Continue ({counts.ready})
        </Button>
      </div>
    </div>
  );
}

function AddrCell({ text, date, res }: { text: string; date: string; res: AddressMap }) {
  const status = addrStatus(text, res);
  return (
    <td className="px-2 py-1 text-slate-600">
      <span className="mr-1">
        {status === 'matched' && <span title="Existing location reused" className="text-emerald-600">✓</span>}
        {status === 'new' && <span title="New location created on import" className="text-sky-600">+</span>}
        {status === 'failed' && <span title="Could not geocode" className="text-red-600">✗</span>}
        {status === 'pending' && <span className="text-slate-300">·</span>}
      </span>
      {cityOf(text)} · {date}
    </td>
  );
}

function MatchBadge({ match }: { match: OrderDraft['customerMatch'] }) {
  if (match === 'code') return <span className="text-[10px] font-medium text-emerald-600">✓ code</span>;
  if (match === 'name') return <span className="text-[10px] font-medium text-amber-600">~ name</span>;
  return <span className="text-[10px] font-medium text-red-600">✗ no match</span>;
}

/** Second comma-segment of an address (usually the town), for a compact column. */
function cityOf(address: string): string {
  const parts = address.split(',').map((s) => s.trim());
  return parts[1] ?? parts[0] ?? '—';
}

// ── Commit ───────────────────────────────────────────────────────────────────

function CommitStep({
  drafts,
  organisationId,
  addressRes,
  patch,
  onBack,
  onDone,
}: {
  drafts: OrderDraft[];
  organisationId: string;
  addressRes: AddressMap;
  patch: (key: string, p: Partial<OrderDraft>) => void;
  onBack: () => void;
  onDone: () => void;
}) {
  const consumeSequence = useConsumeOrganisationSequence();
  const importOrders = useImportOrders();

  const [phase, setPhase] = useState<'idle' | 'running' | 'done'>('idle');
  const [progress, setProgress] = useState<{ done: number; total: number; label: string } | null>(null);

  const committable = useMemo(() => drafts.filter((d) => !d.excluded && isResolvable(d)), [drafts]);
  const counts = useMemo(() => tally(committable), [committable]);
  const failed = committable.filter((d) => d.status_ === 'failed');

  const run = async () => {
    setPhase('running');
    const deps: CommitDeps = {
      organisationId,
      addressResolutions: addressRes,
      consumeOrderNumber: () =>
        consumeSequence.mutateAsync({ organisationId, type: 'Order' }).then((r) => r.formatted),
      importOrders: (orders) => importOrders.mutateAsync({ organisationId, orders }),
      onProgress: (done, total, label) => setProgress({ done, total, label }),
    };
    try {
      await commitOrders(drafts, deps, patch);
    } finally {
      setProgress(null);
      setPhase('done');
    }
  };

  const startCount = committable.filter((d) => d.status_ !== 'created').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {phase === 'running' && progress ? (
          <span className="inline-flex items-center gap-2 text-slate-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {progress.label} {progress.done}/{progress.total}…
          </span>
        ) : (
          <span className="text-slate-600">
            {committable.length} order{committable.length === 1 ? '' : 's'} ready to import
            {phase === 'done' && ' · done'}
          </span>
        )}
        {(counts.created > 0 || phase === 'done') && (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <CircleCheck className="h-4 w-4" /> {counts.created} created
          </span>
        )}
        {counts.failed > 0 && (
          <span className="inline-flex items-center gap-1 text-red-600">
            <CircleX className="h-4 w-4" /> {counts.failed} failed
          </span>
        )}
      </div>

      {failed.length > 0 && (
        <div className="max-h-[20rem] overflow-auto rounded-md border border-slate-200">
          <table className="w-full border-collapse text-xs">
            <thead className="sticky top-0 bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1.5 font-semibold">Job</th>
                <th className="px-2 py-1.5 font-semibold">Customer</th>
                <th className="px-2 py-1.5 font-semibold">Error</th>
              </tr>
            </thead>
            <tbody>
              {failed.map((d) => (
                <tr key={d.key} className="border-t border-slate-100">
                  <td className="px-2 py-1 font-mono text-slate-600">{d.job.jobRef}</td>
                  <td className="px-2 py-1 text-slate-700">{d.customerLabel}</td>
                  <td className="px-2 py-1 text-red-600">{d.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
        <Button type="button" variant="ghost" onClick={onBack} disabled={phase === 'running'}>
          Back
        </Button>
        {phase === 'done' ? (
          <div className="flex items-center gap-2">
            {failed.length > 0 && (
              <Button type="button" variant="secondary" onClick={() => void run()}>
                Retry failed ({failed.length})
              </Button>
            )}
            <Button type="button" onClick={onDone}>
              Done
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            onClick={() => void run()}
            disabled={phase === 'running' || startCount === 0}
          >
            Import {startCount} order{startCount === 1 ? '' : 's'}
          </Button>
        )}
      </div>
    </div>
  );
}

function tally(drafts: OrderDraft[]): Record<DraftStatus, number> {
  const acc = { pending: 0, creating: 0, created: 0, failed: 0, skipped: 0 } as Record<DraftStatus, number>;
  for (const d of drafts) acc[d.status_] += 1;
  return acc;
}
